import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { listContents } from "../../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { get } from "aws-amplify/api";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import moment from "moment";
import ActionsPopover from "../../components/actionsPopover";
import CircularProgress from "@mui/material/CircularProgress";
import { Stack, TextField } from "@mui/material";
import Popover from "@mui/material/Popover";
import {
  Button,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
} from "@mui/material";

// Define a function to transform the data for use in the table
function createData(type, name, additionalInfo) {
  return { type, name, additionalInfo };
}

// Component for nested rows
function NestedRow({ nestedData, type }) {
  const user = useSelector((state) => state.auth.user);
  return (
    <Table sx={{ textAlign: "right" }} size="large">
      <TableHead>
        <TableRow>
          {type === "TV" && (
            <>
              <TableCell>Season</TableCell>
              <TableCell>Episode</TableCell>
              <TableCell>Status</TableCell>
            </>
          )}
          {type === "Music" && (
            <>
              <TableCell>Track Number</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Duration</TableCell>
            </>
          )}
          <TableCell>Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {type === "TV"
          ? nestedData.map((season) => (
              <React.Fragment key={season.id}>
                <TableRow>
                  <TableCell component="th" scope="row">
                    {season.season.seasonNumber}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {season.title}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {season.status === "Feedback"
                      ? (user?.role === "Admin" && "Feedback Sent") ||
                        (user?.role === "ContentProviders" &&
                          "Received Feedback")
                      : season?.status}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <ActionsPopover viewPath={`/content/${season.id}`} />
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          : type === "Music" &&
            nestedData.map((song, index) => (
              <TableRow key={song.id}>
                <TableCell component="th" scope="row">
                  {index + 1}
                </TableCell>
                <TableCell>{song.title}</TableCell>
                <TableCell>{song.duration || "N/A"}</TableCell>
                <TableCell>
                  <ActionsPopover viewPath={`/content/${song.id}`} />
                </TableCell>
              </TableRow>
            ))}
      </TableBody>
    </Table>
  );
}

function Row(props) {
  const { row, providerData, user } = props;
  const [open, setOpen] = React.useState(false);
  const getStatusColor = (status) => {
    if (status === "Action Needed") return "red";
    if (status === "Approved") return "green";
    if (status === "Pending") return "black";
    return "inherit"; 
  };

  const getDisplayStatus = (status) => {
    if (status === "Feedback") {
      if (user?.role === "Admin") return "Feedback Sent";
      if (user?.role === "ContentProviders") return "Action Needed";
    }
    return status;
  };

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          {(row.category === "TV" && row.isSeries === true) ||
          (row.category === "Music" && row.isAlbum === true) ? (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : null}
        </TableCell>
        <TableCell component="th" scope="row">
          {row?.title}
        </TableCell>
        <TableCell>{row.type}</TableCell>
        <TableCell>{row.category}</TableCell>
        <TableCell>{row.agreement?.daysOfAgreement / 30} months</TableCell>
        <TableCell>{row?.currentCycle}</TableCell>
        <TableCell style={{ color: getStatusColor(getDisplayStatus(row?.status)) }}>
          {getDisplayStatus(row?.status)}
        </TableCell>
        <TableCell>{moment(row.createdAt).format("DD MMM YYYY")}</TableCell>
        <TableCell>Pending</TableCell>
        <TableCell>
          {providerData[row.owner] ||
            user?.attributes["custom:name"] ||
            "Unknown"}{" "}
        </TableCell>
        <TableCell>
          <ActionsPopover viewPath={`/content/${row.id}`} />
        </TableCell>
      </TableRow>
      {(row.category === "TV" || row.category === "Music") && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  {row.category === "TV" ? "Seasons and Episodes" : "Songs"}
                </Typography>
                <NestedRow
                  nestedData={row.category === "TV" ? row.seasons : row.songs}
                  type={row.category}
                />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
}

export default function CollapsibleTable() {
  const client = generateClient();
  const user = useSelector((state) => state.auth.user);
  const [success, setSuccess] = useState(false);

  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerData, setProviderData] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    title: "",
    type: "",
    category: "",
    termOfAgreement: "",
    currentCycle: "",
    status: "",
    uploadRequest: "",
    uploaded: "",
    contentProvider: "",
  });
  const [selectedFilterType, setSelectedFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);

  const filterOptions = [
    { value: "title", label: "Title" },
    { value: "type", label: "Type" },
    { value: "category", label: "Category" },
    { value: "termOfAgreement", label: "Term Of Agreement" },
    { value: "currentCycle", label: "Current Cycle" },
    { value: "status", label: "Status" },
    { value: "uploadRequest", label: "Upload Request Date" },
    { value: "contentProvider", label: "Content Provider" },
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterValue(value);
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const handleFilterTypeChange = (e) => {
    setSelectedFilterType(e.target.value);
    setFilterValue(""); // Clear previous filter value when changing filter type
  };

  const handleApplyFilter = () => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [selectedFilterType]: filterValue,
    }));
  };

  const fetchAllContents = async () => {
    try {
      setLoading(true);
      const response = await client.graphql({
        query: listContents,
      });
      const contentList = response.data.listContents.items;

      if (user.role === "Admin") {
        const providers = await Promise.all(
          contentList.map(async (content) => {
            if (content.owner) {
              const providerResponse = await fetchContentProvider(
                content.owner
              );
              return {
                id: content.owner,
                name: providerResponse
                  ? providerResponse["custom:companyName"]
                  : "Unknown",
              };
            }
            return { id: content.owner, name: "Unknown" };
          })
        );
        const providerMap = providers.reduce((acc, provider) => {
          acc[provider.id] = provider.name;
          return acc;
        }, {});
        setProviderData(providerMap);
      }

      setContentList(contentList);
    } catch (error) {
      console.error("Error fetching category:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContentProvider = async (contentProviderId) => {
    try {
      const restOperation = get({
        apiName: "restapi123",
        path: "/getUserData",
        options: {
          queryParams: {
            username: contentProviderId,
          },
        },
      });
      const { body } = await restOperation.response;
      const response = await body.json();
      return response;
    } catch (err) {
      console.log(err, "error");
    }
  };

  useEffect(() => {
    fetchAllContents();
  }, [success]);

  const seriesAndAlbums = contentList?.filter((content) => {
    return content?.isSeries || content?.isAlbum;
  });

  console.log(seriesAndAlbums, "seriesAndAlbums");

  const seasons = contentList?.filter((content) => {
    return content?.seasonId;
  });

  const mappedSeriesAndAlbums = seriesAndAlbums?.map((item) => {
    if (item.isSeries && item.category === "TV") {
      return {
        ...item,
        seasons: seasons?.filter(
          (season) => season?.seriesId === item?.seriesId
        ),
      };
    } else if (item.isAlbum && item.category === "Music") {
      return {
        ...item,
        songs: contentList?.filter((song) => song?.albumId === item?.albumId),
      };
    }
    return item;
  });

  console.log(mappedSeriesAndAlbums, "mappedSeriesAndAlbums");

  useEffect(() => {
    let filtered = mappedSeriesAndAlbums.filter((content) => {
      return (
        (filters.title === "" ||
          content.title.toLowerCase().includes(filters.title.toLowerCase())) &&
        (filters.type === "" ||
          content.type.toLowerCase().includes(filters.type.toLowerCase())) &&
        (filters.category === "" ||
          content.category
            .toLowerCase()
            .includes(filters.category.toLowerCase())) &&
        (filters.termOfAgreement === "" ||
          content.agreement?.daysOfAgreement
            ?.toString()
            .includes(filters.termOfAgreement)) &&
        (filters.currentCycle === "" ||
          content.currentCycle.toString().includes(filters.currentCycle)) &&
        (filters.status === "" ||
          content.status
            .toLowerCase()
            .includes(filters.status.toLowerCase())) &&
        (filters.uploadRequest === "" ||
          moment(content.createdAt)
            .format("DD MMM YYYY")
            .includes(filters.uploadRequest)) &&
        (filters.contentProvider === "" ||
          providerData[content.owner]
            ?.toLowerCase()
            .includes(filters.contentProvider.toLowerCase()))
      );
    });
    setFilteredData(filtered);
  }, [filters, contentList, providerData]);

  const columns = [
    { id: "title", label: "Title" },
    { id: "type", label: "Type" },
    { id: "category", label: "Category" },
    { id: "termOfAgreement", label: "Term Of Agreement" },
    { id: "currentCycle", label: "Current Cycle" },
    { id: "status", label: "Status" },
    { id: "uploadRequest", label: "Upload Request" },
    { id: "uploaded", label: "Uploaded" },
    { id: "contentProvider", label: "Content Provider" },
    { id: "actions", label: "Actions" },
  ];

  return (
    <>
      <TableContainer component={Paper}>
        <Button
          sx={{ marginY: "10px" }}
          aria-describedby={id}
          variant="contained"
          onClick={handleClick}
        >
          Filter
        </Button>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          sx={{ padding: "10px" }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Stack
            direction={"row"}
            sx={{ width: "500px", padding: "10px" }}
            gap={3}
          >
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Filter Field</InputLabel>
              <Select
                value={selectedFilterType}
                onChange={handleFilterTypeChange}
                label="Select Filter Field"
              >
                {filterOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedFilterType && (
              <TextField
                label={`Search ${
                  filterOptions.find(
                    (option) => option.value === selectedFilterType
                  )?.label
                }`}
                variant="outlined"
                value={filterValue}
                onChange={handleFilterChange}
                fullWidth
                margin="normal"
                name={selectedFilterType}
              />
            )}
          </Stack>
        </Popover>
        <Table aria-label="collapsible table">
          <TableHead
            sx={{
              backgroundColor: "brown !important",
              "& .MuiTableHead-root": {
                backgroundColor: "purple !important",
              },
            }}
          >
            <TableRow sx={{ backgroundColor: "purple !important" }}>
              <TableCell
                sx={{ backgroundColor: "#5D5D5D", color: "secondary.main" }}
              />
              {columns.map((column) => (
                <TableCell
                  sx={{ backgroundColor: "#5D5D5D", color: "secondary.main" }}
                  key={column.id}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ width: "100%" }}>
            {loading ? (
              <TableRow>
                <TableCell component="th" scope="row" colSpan={11}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100px"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredData.map((content) => (
                  <Row
                    key={content.id}
                    row={content}
                    user={user}
                    providerData={providerData}
                  />
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
